<template>
  <div ref="root" class="bitts-option-list">
    <div v-if="useSearch || $slots['search-top']" class="search-wrapper">
      <slot name="search-top" />
      <BittsInput
        v-if="useSearch"
        v-bind="searchInputAttrs"
        :model-value="searchTerm"
        name="searchTerm"
        size="small"
        type="search"
        class="bitts-option-list__search-input"
        :placeholder="searchPlaceholder"
        @update:model-value="$emit('update:searchTerm', $event)"
        @keydown="onListKeydown"
      />
    </div>

    <template v-if="onlyOptions.length > 0">
      <div
        ref="scrollableSection"
        class="options-wrapper"
        :class="{
          'scrollable-down':
            !useVirtualScroll && isScrollable && !arrivedState.bottom,
          'scrollable-up':
            !useVirtualScroll && isScrollable && !arrivedState.top,
        }"
      >
        <!-- Virtual scroll mode -->
        <!-- eslint-disable-next-line vuejs-accessibility/interactive-supports-focus -- tabindex is bound -->
        <div
          v-if="useVirtualScroll"
          ref="listbox"
          v-bind="listboxAttrs"
          role="listbox"
          :tabindex="useSearch ? -1 : 0"
          class="virtual-scroll-wrapper"
          :class="{ 'options-list--grouped': showGroupCheckbox }"
          @keydown.self="onListKeydown"
          @focus="activateSelectedOrFirst"
          @pointerdown="markPointerFocus"
        >
          <BittsVirtualScrollList
            ref="virtualScrollList"
            :options="displayOptions"
            :max-height="virtualScrollMaxHeight"
            :item-height="virtualScrollItemHeight"
            :use-scroll-fade="true"
            :get-item-key="getKey"
          >
            <template #default="{ item }">
              <div
                v-if="isGroup(item)"
                class="virtual-group-heading"
                :class="{
                  'group-heading--interactive': showGroupCheckbox,
                }"
                :style="{ height: `${virtualScrollItemHeight}px` }"
                role="presentation"
                @click="showGroupCheckbox && toggleGroupCollapse(item)"
              >
                <button
                  v-if="showGroupCheckbox"
                  type="button"
                  class="group-heading__toggle"
                  :aria-expanded="isGroupOpen(item)"
                  :aria-label="item.label"
                  @click.stop="toggleGroupCollapse(item)"
                >
                  <FontAwesomeIcon
                    :icon="['fak', 'chevron-down']"
                    :style="{
                      width: '12px',
                      height: '12px',
                      transform: isGroupOpen(item)
                        ? 'rotate(0deg)'
                        : 'rotate(-90deg)',
                    }"
                    class="group-heading__chevron"
                  />
                </button>
                <BittsCheckbox
                  v-if="showGroupCheckbox"
                  :checked="isGroupAllSelected(item)"
                  :indeterminate="isGroupIndeterminate(item)"
                  :aria-label="`Select all ${item.label}`"
                  @input="handleGroupCheckboxChange(item, $event)"
                  @click.stop
                />
                <span aria-hidden="true">{{ item.label }}</span>
              </div>

              <div
                v-else-if="isDivider(item)"
                class="virtual-divider"
                aria-hidden="true"
                :style="{ height: `${virtualScrollItemHeight}px` }"
              >
                <hr class="divider" />
              </div>

              <BittsOptionListItem
                v-else
                v-bind="itemAttrs(item)"
                class="w-full"
                :aria-setsize="navigableOptions.length"
                :aria-posinset="navigableOptions.indexOf(item) + 1"
                @click="handleItemClick(item, $event)"
                @checkbox-change="handleCheckboxChange(item, $event)"
              >
                <template v-if="$slots.prefix" #prefix>
                  <slot name="prefix" :option="item" />
                </template>

                <template #default>
                  <slot :option="item">{{ item.label }}</slot>
                </template>

                <template #suffix>
                  <slot name="suffix" :option="item" />
                </template>
              </BittsOptionListItem>
            </template>
          </BittsVirtualScrollList>
        </div>

        <!-- Standard mode (grouped) -->
        <!-- eslint-disable-next-line vuejs-accessibility/interactive-supports-focus -- tabindex is bound -->
        <ul
          v-else-if="showGroupCheckbox"
          ref="listbox"
          v-bind="listboxAttrs"
          role="listbox"
          :tabindex="useSearch ? -1 : 0"
          class="flex flex-col gap-2 py-4 px-6 options-list--grouped"
          @keydown.self="onListKeydown"
          @focus="activateSelectedOrFirst"
          @pointerdown="markPointerFocus"
        >
          <template
            v-for="(entry, entryIdx) in groupedItems"
            :key="getEntryKey(entry, entryIdx)"
          >
            <li
              v-if="entry.type === 'group'"
              role="group"
              :aria-label="entry.group.label"
              class="flex flex-col gap-2"
            >
              <div
                class="group-heading group-heading--interactive"
                role="presentation"
                @click="toggleGroupCollapse(entry.group)"
              >
                <button
                  type="button"
                  class="group-heading__toggle"
                  :aria-expanded="isGroupOpen(entry.group)"
                  :aria-label="entry.group.label"
                  @click.stop="toggleGroupCollapse(entry.group)"
                >
                  <FontAwesomeIcon
                    :icon="['fak', 'chevron-down']"
                    :style="{
                      width: '12px',
                      height: '12px',
                      transform: isGroupOpen(entry.group)
                        ? 'rotate(0deg)'
                        : 'rotate(-90deg)',
                    }"
                    class="group-heading__chevron"
                  />
                </button>
                <BittsCheckbox
                  :checked="isGroupAllSelected(entry.group)"
                  :indeterminate="isGroupIndeterminate(entry.group)"
                  :aria-label="`Select all ${entry.group.label}`"
                  @input="handleGroupCheckboxChange(entry.group, $event)"
                  @click.stop
                />
                <span aria-hidden="true">{{ entry.group.label }}</span>
              </div>
              <Transition name="group-collapse">
                <ul
                  v-show="isGroupOpen(entry.group) || isSearching"
                  role="none"
                  class="group-children"
                >
                  <BittsOptionListItem
                    v-for="(child, childIdx) in entry.children"
                    :key="`option-${String(child.value)}-${childIdx}`"
                    v-bind="itemAttrs(child)"
                    @click="handleItemClick(child, $event)"
                    @checkbox-change="handleCheckboxChange(child, $event)"
                  >
                    <template v-if="$slots.prefix" #prefix>
                      <slot name="prefix" :option="child" />
                    </template>
                    <template #default>
                      <slot :option="child">{{ child.label }}</slot>
                    </template>
                    <template #suffix>
                      <slot name="suffix" :option="child" />
                    </template>
                  </BittsOptionListItem>
                </ul>
              </Transition>
            </li>
            <BittsOptionListItem
              v-else-if="entry.type === 'item'"
              v-bind="itemAttrs(entry.item)"
              @click="handleItemClick(entry.item, $event)"
              @checkbox-change="handleCheckboxChange(entry.item, $event)"
            >
              <template v-if="$slots.prefix" #prefix>
                <slot name="prefix" :option="entry.item" />
              </template>
              <template #default>
                <slot :option="entry.item">{{ entry.item.label }}</slot>
              </template>
              <template #suffix>
                <slot name="suffix" :option="entry.item" />
              </template>
            </BittsOptionListItem>
          </template>
        </ul>

        <!-- Standard mode (flat) -->
        <!-- eslint-disable-next-line vuejs-accessibility/interactive-supports-focus -- tabindex is bound -->
        <ul
          v-else
          ref="listbox"
          v-bind="listboxAttrs"
          role="listbox"
          :tabindex="useSearch ? -1 : 0"
          class="flex flex-col gap-2 py-4 px-6"
          @keydown.self="onListKeydown"
          @focus="activateSelectedOrFirst"
          @pointerdown="markPointerFocus"
        >
          <template
            v-for="(entry, entryIdx) in flatEntries"
            :key="getEntryKey(entry, entryIdx)"
          >
            <li
              v-if="entry.type === 'group' && !entry.children.length"
              class="group-heading"
              aria-hidden="true"
            >
              {{ entry.group.label }}
            </li>
            <li
              v-else-if="entry.type === 'group'"
              role="group"
              :aria-label="entry.group.label"
              class="flex flex-col gap-2"
            >
              <div
                v-if="entry.hasHeading"
                class="group-heading"
                aria-hidden="true"
              >
                {{ entry.group.label }}
              </div>
              <ul role="none" class="flex flex-col gap-2">
                <BittsOptionListItem
                  v-for="child in entry.children"
                  :key="getKey(child, 0)"
                  v-bind="itemAttrs(child)"
                  @click="handleItemClick(child, $event)"
                  @checkbox-change="handleCheckboxChange(child, $event)"
                >
                  <template v-if="$slots.prefix" #prefix>
                    <slot name="prefix" :option="child" />
                  </template>
                  <template #default>
                    <slot :option="child">{{ child.label }}</slot>
                  </template>
                  <template #suffix>
                    <slot name="suffix" :option="child" />
                  </template>
                </BittsOptionListItem>
              </ul>
            </li>

            <li v-else-if="entry.type === 'divider'" aria-hidden="true">
              <hr class="divider" />
            </li>

            <BittsOptionListItem
              v-else
              v-bind="itemAttrs(entry.item)"
              @click="handleItemClick(entry.item, $event)"
              @checkbox-change="handleCheckboxChange(entry.item, $event)"
            >
              <template v-if="$slots.prefix" #prefix>
                <slot name="prefix" :option="entry.item" />
              </template>

              <template #default>
                <slot :option="entry.item">{{ entry.item.label }}</slot>
              </template>

              <template #suffix>
                <slot name="suffix" :option="entry.item" />
              </template>
            </BittsOptionListItem>
          </template>
        </ul>
      </div>
    </template>

    <div v-else-if="searchTerm" class="bitts-option-list__empty-state">
      <BittsEmptyState
        :font-awesome-icon="['fak', 'search']"
        :font-awesome-icon-style="{ height: '26px', width: '26px' }"
        font-awesome-icon-class="bitts-option-list__empty-state-icon"
        title="No results"
        description="Try a different search term"
        class="mx-auto w-[280px] border-none"
      />
    </div>

    <div v-if="$slots.footer" class="bitts-option-list__footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
  generic="
    TOption extends { label: string; value: any; disabled?: boolean },
    TInput extends
      | TOption
      | { label: string; options: readonly any[]; useBorder?: boolean }
      | { type: 'group'; label: string; useBorder?: boolean }
      | { type: 'divider' }
  "
>
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useScroll } from '@vueuse/core';
import { computed, nextTick, ref, useId, useTemplateRef, watch } from 'vue';

import BittsCheckbox from '../BittsCheckbox/BittsCheckbox.vue';
import BittsEmptyState from '../BittsEmptyState/BittsEmptyState.vue';
import BittsInput from '../BittsInput/BittsInput.vue';
import BittsVirtualScrollList from '../BittsVirtualScrollList/BittsVirtualScrollList.vue';
import { useListboxNavigation } from '../composables/useListboxNavigation';
import { BittsIcon } from '../types';

import BittsOptionListItem from './BittsOptionListItem.vue';

// ============================================================================
// Type Definitions
// ============================================================================

export type OptionItem<T> = T & {
  label: string;
  value: any;
  disabled?: boolean;
  faIcon?: BittsIcon;
  action?: () => void;
};

// Input type: nested groups are inferred by having an `options` property
export type NestedGroupItem<T> = {
  label: string;
  useBorder?: boolean;
  options: readonly (OptionItem<T> | NestedGroupItem<T>)[];
};

// Internal types: used after flattening nested groups
export type GroupItem = {
  type: 'group';
  label: string;
  useBorder?: boolean;
  childValues?: unknown[];
};
export type DividerItem = { type: 'divider' };
export type ListItem<T> = OptionItem<T> | GroupItem | DividerItem;

type ExtractLeafOption<T> = T extends { options: readonly (infer U)[] }
  ? ExtractLeafOption<U>
  : T extends { label: string; value: any }
    ? T
    : never;

type InferredOption = ExtractLeafOption<TInput>;
type FlatItem = ListItem<InferredOption>;
type Option = OptionItem<InferredOption>;
type NestedGroup = {
  label: string;
  useBorder?: boolean;
  options: readonly any[];
  initialOptionsExist?: boolean;
};

// ============================================================================
// Props, Emits & Slots
// ============================================================================

const props = withDefaults(
  defineProps<{
    options: readonly TInput[];
    mode?: 'single' | 'multi';
    variant?: 'default' | 'upsell';
    showCheckbox?: boolean;
    useSelectAll?: boolean;
    useSearch?: boolean;
    searchTerm?: string;
    searchPlaceholder?: string;
    /** Enable virtual scrolling for large lists */
    useVirtualScroll?: boolean;
    /** Max height for virtual scroll container (default: 294) */
    virtualScrollMaxHeight?: number;
    /** Item height for virtual scroll (default: 36) */
    virtualScrollItemHeight?: number;
    /** Set of parent option values that have selected children */
    activeParentValues?: Set<unknown>;
    /** Show checkboxes on group headers for bulk group selection */
    showGroupCheckbox?: boolean;
    /** Names the listbox, for example after the control that opens it */
    ariaLabel?: string;
  }>(),
  {
    mode: 'multi',
    variant: 'default',
    showCheckbox: true,
    useSelectAll: false,
    useSearch: false,
    searchTerm: '',
    searchPlaceholder: 'Search',
    useVirtualScroll: false,
    virtualScrollMaxHeight: 294,
    virtualScrollItemHeight: 30,
    activeParentValues: () => new Set(),
    showGroupCheckbox: false,
    ariaLabel: undefined,
  },
);

const emit = defineEmits<{
  change: [values: unknown[] | unknown | undefined];
  'item-click': [item: Option, event: MouseEvent];
  'update:searchTerm': [value: string];
}>();

defineSlots<{
  default: (props: { option: Option }) => unknown;
  prefix: (props: { option: Option }) => unknown;
  suffix: (props: { option: Option }) => unknown;
  'search-top': () => unknown;
  footer: () => unknown;
}>();

const model = defineModel<unknown[] | unknown | undefined>();

// ============================================================================
// Type Guards
// ============================================================================

function isGroup(item: FlatItem): item is GroupItem {
  return 'type' in item && item.type === 'group';
}

function isDivider(item: FlatItem): item is DividerItem {
  return 'type' in item && item.type === 'divider';
}

function isNestedGroup(item: unknown): item is NestedGroup {
  return (
    typeof item === 'object' &&
    item !== null &&
    'options' in item &&
    Array.isArray((item as NestedGroup).options) &&
    !(item as NestedGroup).initialOptionsExist
  );
}

function isOption(item: FlatItem): item is Option {
  return !('type' in item);
}

// ============================================================================
// Flatten Nested Options
// ============================================================================

function flattenGroup(group: NestedGroup, isFirst: boolean): FlatItem[] {
  const result: FlatItem[] = [];
  const childValues: unknown[] = [];

  if (group.useBorder && !isFirst) {
    result.push({ type: 'divider' });
  }

  const childItems: FlatItem[] = [];
  for (const opt of group.options) {
    if (isNestedGroup(opt)) {
      const nested = flattenGroup(opt, false);
      for (const fi of nested) {
        if (isOption(fi)) childValues.push(fi.value);
      }
      childItems.push(...nested);
    } else {
      childValues.push((opt as Option).value);
      childItems.push(opt as Option);
    }
  }

  if (group.label) {
    result.push({
      type: 'group',
      label: group.label,
      useBorder: group.useBorder,
      childValues,
    });
  }

  result.push(...childItems);
  return result;
}

const flattenedOptions = computed<FlatItem[]>(() => {
  return props.options.flatMap((item, index) =>
    isNestedGroup(item) ? flattenGroup(item, index === 0) : [item as FlatItem],
  );
});

const normalizedOptions = computed<FlatItem[]>(() => {
  const search = props.searchTerm.trim().toLowerCase();
  if (!search) return flattenedOptions.value;

  const result: FlatItem[] = [];
  let divider: DividerItem | null = null;
  let group: GroupItem | null = null;

  const flush = () => {
    if (divider && result.length) result.push(divider);
    divider = null;

    if (group) {
      result.push(group);
      group = null;
    }
  };

  for (const item of flattenedOptions.value) {
    if (isDivider(item)) {
      divider = item;
      continue;
    }

    if (isGroup(item)) {
      group = item;
      continue;
    }

    if (isOption(item) && item.label.toLowerCase().includes(search)) {
      flush();
      result.push(item);
    }
  }

  return result;
});

// ============================================================================
// Selection State
// ============================================================================

const SELECT_ALL_VALUE = '__select_all__' as const;

const selectedSet = computed(() => {
  if (props.mode === 'single') {
    return new Set(model.value != null ? [model.value] : []);
  }
  // Ensure model.value is an array in multi mode
  const values = Array.isArray(model.value) ? model.value : [];
  return new Set(values);
});

const onlyOptions = computed(() => normalizedOptions.value.filter(isOption));
const enabledOptions = computed(() =>
  onlyOptions.value.filter((opt) => !opt.disabled),
);

const allSelected = computed(
  () =>
    enabledOptions.value.length > 0 &&
    enabledOptions.value.every((opt) => selectedSet.value.has(opt.value)),
);

const itemsWithSelectAll = computed<readonly FlatItem[]>(() => {
  const shouldShowSelectAll =
    props.useSelectAll &&
    props.showCheckbox &&
    props.mode === 'multi' &&
    onlyOptions.value.length > 0;

  if (!shouldShowSelectAll) return normalizedOptions.value;

  return [
    {
      label: '(Select All)',
      value: SELECT_ALL_VALUE,
      disabled: false,
    } as Option,
    ...normalizedOptions.value,
  ];
});

// ============================================================================
// Selection Logic
// ============================================================================

function setSelected(
  values: InferredOption['value'][] | InferredOption['value'] | undefined,
) {
  model.value = values;
  emit('change', values);
}

function toggleOption(option: Option, checked?: boolean) {
  const set = new Set(selectedSet.value);
  const shouldAdd = checked ?? !set.has(option.value);

  if (shouldAdd) {
    set.add(option.value);
  } else {
    set.delete(option.value);
  }

  setSelected(Array.from(set));
}

function handleSelectAllChange(checked: boolean) {
  setSelected(checked ? enabledOptions.value.map((opt) => opt.value) : []);
}

function handleCheckboxChange(option: Option, checked: boolean) {
  if (props.mode !== 'multi' || !props.showCheckbox || option.disabled) return;

  if (option.value === SELECT_ALL_VALUE) {
    handleSelectAllChange(checked);
  } else {
    toggleOption(option, checked);
  }
}

function handleItemClick(item: FlatItem, event: MouseEvent | KeyboardEvent) {
  if (!isOption(item) || item.disabled) return;
  setActiveIndex(navigableOptions.value.indexOf(item));

  // Emit event to allow parent to intercept (e.g., for navigation items)
  emit('item-click', item, event as MouseEvent);
  if (event.defaultPrevented) return;

  if (props.mode === 'single') {
    setSelected(item.value);
    item.action?.();
    return;
  }

  item.value === SELECT_ALL_VALUE
    ? handleSelectAllChange(!allSelected.value)
    : toggleOption(item);
}

function isSelected(item: FlatItem): boolean {
  if (!isOption(item)) return false;
  if (item.value === SELECT_ALL_VALUE) return allSelected.value;
  return selectedSet.value.has(item.value);
}

function hasActiveChildren(item: FlatItem): boolean {
  if (!isOption(item)) return false;
  return props.activeParentValues.has(item.value);
}

const isSearching = computed(() => props.searchTerm.trim().length > 0);

const visibleChildValuesByGroup = computed(() => {
  const map = new Map<string, unknown[]>();
  let currentLabel: string | null = null;

  for (const item of normalizedOptions.value) {
    if (isGroup(item)) {
      currentLabel = item.label;
      if (!map.has(currentLabel)) map.set(currentLabel, []);
    } else if (currentLabel && isOption(item)) {
      map.get(currentLabel)?.push(item.value);
    }
  }

  return map;
});

function getGroupChildValues(group: GroupItem): unknown[] {
  if (!isSearching.value) return group.childValues ?? [];
  return visibleChildValuesByGroup.value.get(group.label) ?? [];
}

function isGroupAllSelected(group: GroupItem): boolean {
  const values = getGroupChildValues(group);
  if (!values.length) return false;
  return values.every((v) => selectedSet.value.has(v));
}

function isGroupIndeterminate(group: GroupItem): boolean {
  const values = getGroupChildValues(group);
  if (!values.length) return false;
  const selectedCount = values.filter((v) => selectedSet.value.has(v)).length;
  return selectedCount > 0 && selectedCount < values.length;
}

function handleGroupCheckboxChange(group: GroupItem, checked: boolean) {
  const values = getGroupChildValues(group);
  if (!values.length) return;
  const set = new Set(selectedSet.value);
  if (checked) {
    values.forEach((v) => set.add(v));
  } else {
    values.forEach((v) => set.delete(v));
  }
  setSelected(Array.from(set));
}

// ============================================================================
// Group Collapse
// ============================================================================

const collapsedGroups = ref(new Set<string>());

function toggleGroupCollapse(group: GroupItem) {
  const next = new Set(collapsedGroups.value);
  if (next.has(group.label)) {
    next.delete(group.label);
  } else {
    next.add(group.label);
  }
  collapsedGroups.value = next;
}

const displayOptions = computed<readonly FlatItem[]>(() => {
  const items = itemsWithSelectAll.value;
  if (
    !props.showGroupCheckbox ||
    !collapsedGroups.value.size ||
    isSearching.value
  ) {
    return items;
  }

  const result: FlatItem[] = [];
  let isCurrentGroupCollapsed = false;

  for (const item of items) {
    if (isGroup(item)) {
      isCurrentGroupCollapsed = collapsedGroups.value.has(item.label);
      result.push(item);
    } else if (isDivider(item)) {
      result.push(item);
    } else if (!isCurrentGroupCollapsed) {
      result.push(item);
    }
  }

  return result;
});

type GroupEntry = {
  type: 'group';
  group: GroupItem;
  children: Option[];
  hasHeading: boolean;
};
type GroupedEntry =
  | GroupEntry
  | { type: 'item'; item: Option }
  | { type: 'divider' };

const groupedItems = computed<GroupedEntry[]>(() => {
  const result: GroupedEntry[] = [];
  let current: GroupedEntry | null = null;

  for (const item of itemsWithSelectAll.value) {
    if (isGroup(item)) {
      current = { type: 'group', group: item, children: [], hasHeading: true };
      result.push(current);
    } else if (current?.type === 'group' && isOption(item)) {
      current.children.push(item);
    } else if (isOption(item)) {
      result.push({ type: 'item', item });
    }
  }

  return result;
});

function isPositionalGroup(group: GroupItem | undefined) {
  return !!group && !group.childValues;
}

/**
 * Groups for the flat list. Groups flattened from nested options know their
 * members, so an option after a nested group goes back to the enclosing
 * group, in a group without a heading. Groups passed in already flattened do
 * not, and hold the options that follow them until the next group or divider.
 */
const flatEntries = computed<GroupedEntry[]>(() => {
  const result: GroupedEntry[] = [];
  const openGroups: GroupItem[] = [];
  let current: GroupEntry | null = null;

  function closePositionalGroups() {
    while (isPositionalGroup(openGroups.at(-1))) openGroups.pop();
  }

  for (const item of itemsWithSelectAll.value) {
    if (isGroup(item)) {
      closePositionalGroups();
      openGroups.push(item);
      current = { type: 'group', group: item, children: [], hasHeading: true };
      result.push(current);
      continue;
    }
    if (isDivider(item)) {
      closePositionalGroups();
      current = null;
      result.push({ type: 'divider' });
      continue;
    }
    while (
      openGroups.length &&
      !isPositionalGroup(openGroups.at(-1)) &&
      !openGroups.at(-1)?.childValues?.includes(item.value)
    ) {
      openGroups.pop();
    }
    const owner = openGroups.at(-1);
    if (!owner) {
      current = null;
      result.push({ type: 'item', item });
      continue;
    }
    if (current?.group !== owner) {
      current = {
        type: 'group',
        group: owner,
        children: [],
        hasHeading: false,
      };
      result.push(current);
    }
    current.children.push(item);
  }

  return result;
});

function isGroupOpen(group: GroupItem): boolean {
  return !collapsedGroups.value.has(group.label);
}

// ============================================================================
// Template Helpers
// ============================================================================

function getKey(item: FlatItem, index: number): string {
  if (isGroup(item)) return `group-${item.label}-${index}`;
  if (isDivider(item)) return `divider-${index}`;
  return `option-${String(item.value)}`;
}

function getEntryKey(entry: GroupedEntry, index: number): string {
  if (entry.type === 'group') return getKey(entry.group, index);
  if (entry.type === 'divider') return getKey(entry, index);
  return getKey(entry.item, index);
}

function isSelectAll(item: FlatItem): boolean {
  return isOption(item) && item.value === SELECT_ALL_VALUE;
}

// ============================================================================
// Scroll Fade
// ============================================================================

const scrollableSection = useTemplateRef('scrollableSection');
const { arrivedState, measure } = useScroll(scrollableSection);
const isScrollable = computed(() => !arrivedState.bottom || !arrivedState.top);

watch(
  displayOptions,
  async () => {
    await nextTick();
    measure();
  },
  { immediate: true },
);

// ============================================================================
// Keyboard Navigation
// ============================================================================

const listId = useId();
const root = useTemplateRef<HTMLElement>('root');
const listbox = useTemplateRef<HTMLElement>('listbox');
const virtualScrollList = useTemplateRef('virtualScrollList');

const navigableOptions = computed(() => displayOptions.value.filter(isOption));

/** Keyed by position before filtering, so an option keeps its id while searching */
const optionIds = computed(() => {
  const ids = new Map<unknown, string>([
    [SELECT_ALL_VALUE, `${listId}-select-all`],
  ]);
  flattenedOptions.value.filter(isOption).forEach((option, index) => {
    ids.set(option.value, `${listId}-option-${index}`);
  });
  return ids;
});

function getOptionId(option: Option): string {
  return optionIds.value.get(option.value) ?? `${listId}-option`;
}

const focusTarget = computed<HTMLElement | null | undefined>(() =>
  props.useSearch
    ? root.value?.querySelector<HTMLElement>(
        '.bitts-option-list__search-input input',
      )
    : listbox.value,
);

/**
 * Enter and Space activate the option exactly as a click does. A virtual row
 * scrolled out of view is not rendered, so it gets a click event of its own.
 */
function selectOption(option: Option) {
  const row = document.getElementById(getOptionId(option));
  if (row) {
    row.click();
    return;
  }
  handleItemClick(option, new MouseEvent('click', { cancelable: true }));
}

function scrollToOption(option: Option) {
  if (props.useVirtualScroll) {
    virtualScrollList.value?.scrollToIndex(
      displayOptions.value.indexOf(option),
    );
    return;
  }
  document
    .getElementById(getOptionId(option))
    ?.scrollIntoView?.({ block: 'nearest' });
}

const { activeId, activeIndex, isActive, onListKeydown, setActiveIndex } =
  useListboxNavigation<Option>({
    isOpen: true,
    options: navigableOptions,
    getOptionId,
    trigger: focusTarget,
    list: root,
    listId,
    isOptionDisabled: (option) => !!option.disabled,
    getOptionLabel: (option) => option.label,
    scrollToOption,
    componentName: 'BittsOptionList',
    onSelect: selectOption,
  });

const listboxAttrs = computed(
  () =>
    ({
      id: listId,
      'aria-label': props.ariaLabel,
      'aria-multiselectable': props.mode === 'multi' ? 'true' : undefined,
      'aria-activedescendant': activeId.value,
    }) as const,
);

const searchInputAttrs = computed(() => {
  const hasOptions = onlyOptions.value.length > 0;
  return {
    role: 'combobox',
    'aria-label': props.searchPlaceholder,
    'aria-autocomplete': 'list',
    'aria-expanded': hasOptions ? 'true' : 'false',
    'aria-controls': hasOptions ? listId : undefined,
    'aria-activedescendant': activeId.value,
  } as const;
});

function itemAttrs(item: Option) {
  return {
    id: getOptionId(item),
    option: item,
    selected: isSelected(item),
    hasActiveChildren: hasActiveChildren(item),
    showCheckbox: props.mode === 'multi' && props.showCheckbox,
    noRowStyles: props.mode === 'multi' && props.showCheckbox,
    bold: isSelectAll(item),
    variant: props.variant,
    faIcon: item.faIcon,
    isOption: true,
    isActive: isActive(navigableOptions.value.indexOf(item)),
  };
}

let isFocusFromPointer = false;

/** Pointer focus lands before the click, which then picks the option itself. */
function markPointerFocus() {
  isFocusFromPointer = true;
  setTimeout(() => {
    isFocusFromPointer = false;
  });
}

function activateSelectedOrFirst() {
  if (isFocusFromPointer || activeIndex.value >= 0) return;
  const selectableOptions = navigableOptions.value.filter(
    (option) => !option.disabled,
  );
  const start = selectableOptions.find(isSelected) ?? selectableOptions[0];
  if (start) setActiveIndex(navigableOptions.value.indexOf(start));
}

defineExpose({ listId, activeId, getOptionId, onListKeydown });
</script>

<style scoped lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";

.search-wrapper {
  @apply p-8 pb-4;
}

.group-heading {
  @apply px-8 py-2 text-sm text-neutral-text-weak;
}

.group-heading--interactive {
  @apply flex items-center gap-8 cursor-pointer rounded-6;
}

.group-heading--interactive:hover {
  @apply bg-neutral-background-weak;
}

.group-heading__toggle {
  @apply flex shrink-0 cursor-pointer;
}

.group-heading__chevron {
  @apply shrink-0;
  transition: transform 200ms ease-in-out;
}

.group-children {
  @apply flex flex-col gap-2;
}

.group-collapse-enter-active,
.group-collapse-leave-active {
  @apply overflow-hidden;
  transition: all 200ms ease-in-out;
}

.group-collapse-enter-from,
.group-collapse-leave-to {
  @apply max-h-0 opacity-0;
}

.group-collapse-enter-to,
.group-collapse-leave-from {
  @apply max-h-[500px] opacity-100;
}

.bitts-option-list:focus-within :deep(.list-item.active) {
  @apply outline-2 -outline-offset-2 outline-neutral-border-focus;
}

.options-list--grouped :deep(.list-item) {
  padding-left: 28px;
}

.divider {
  @apply my-2 border-neutral-border -mx-4;
}

.options-wrapper {
  @apply max-h-[294px] overflow-y-auto;

  &:has(.virtual-scroll-wrapper) {
    @apply max-h-none overflow-visible;
  }
}

.options-wrapper.scrollable-up.scrollable-down {
  mask-image: linear-gradient(to bottom, transparent 0%, black 15%),
    linear-gradient(to top, transparent 0%, black 15%);
  mask-composite: intersect;
}

.options-wrapper.scrollable-up:not(.scrollable-down) {
  mask-image: linear-gradient(to bottom, transparent 0%, black 15%);
}

.options-wrapper.scrollable-down:not(.scrollable-up) {
  mask-image: linear-gradient(to top, transparent 0%, black 15%);
}

.virtual-scroll-wrapper {
  @apply py-4 pl-[6px] pr-0;

  :deep(.list-item) {
    @apply mr-[6px] block;
  }
}

.virtual-group-heading {
  @apply px-8 flex items-center text-sm text-neutral-text-weak w-full;
}

.virtual-divider {
  @apply flex items-center w-full pr-[6px];

  .divider {
    @apply w-full my-0;
  }
}

.bitts-option-list__footer {
  @apply border-t border-neutral-border p-6;
}

.bitts-option-list__empty-state {
  @apply py-8 text-center;
}

:deep(.bitts-option-list__empty-state-icon) {
  @apply text-neutral-text-weak p-20 rounded-[36px];
  background: radial-gradient(
      136.96% 69.92% at 50.09% -12.42%,
      theme(colors.neutral.accent / 0.2) 0%,
      theme(colors.neutral.accent / 0) 100%
    ),
    linear-gradient(0deg, theme(colors.white), theme(colors.white));
}
</style>
